import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Dropdown, Input, message, Modal, Progress, Switch, Tag, Tooltip, TreeSelect} from 'antd';
import {DeleteOutlined, DownloadOutlined, FolderOpenOutlined, MoreOutlined, SearchOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {
  buildDirTree,
  isSystemDirectory,
  mapDirTreeTitles,
  normalizeShareDirectory,
  saveActiveDirectory,
  sortDirectories,
} from '../../utils/dirTree';
import {isImg} from "../../helpers/img";
import {isValidUrl} from "../../helpers/url";
import {
  copyToClipboardAsync,
  getStoredWatermarkImageUrl,
  getStoredWatermarkText,
  saveWatermarkImageUrl,
  saveWatermarkText,
  urlSafeBase64Encode
} from '../../helpers/clipboard';
import { useTranslation } from '../../hooks/useTranslation';
import { useIsPc } from '../../hooks/useIsPc';
import {ROUTES} from '../../routes/constants';


interface BatchActionsProps {
  selectedIds: string[];
  setSelectedIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  list: any[];
  loadHistory: () => void;
  currentDir: string;
  onDirChange: (dir: string) => void;
  onKeywordChange: (keyword: string) => void;
  onSearch: (kw: string) => void;
}

const isAllDirectory = (dir?: string) => dir === '__all__' || dir === '全部目录';

interface PreparedDownloadItem {
  id: number;
  fileName: string;
  ticket: string;
}

interface BatchDownloadProgress {
  current: number;
  total: number;
  success: number;
  failed: number;
}

const BatchActions: React.FC<BatchActionsProps> = ({
  selectedIds, setSelectedIds, list, loadHistory, currentDir, onDirChange, onKeywordChange, onSearch
}) => {
  const { t } = useTranslation();
  const isPc = useIsPc();
  const navigate = useNavigate();
  const [manualCopyUrl, setManualCopyUrl] = useState<string | null>(null);
  const [dirLoading, setDirLoading] = useState(false);
  const [dirList, setDirList] = useState<string[]>([]);
  const [sharedDirectoryPaths, setSharedDirectoryPaths] = useState<Set<string>>(new Set());
  const [internalDir, setInternalDir] = useState(currentDir);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveTargetDir, setMoveTargetDir] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);

  const [generateGalleryModalOpen, setGenerateGalleryModalOpen] = useState(false);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [galleryBannerUrl, setGalleryBannerUrl] = useState('');
  const [galleryDescription, setGalleryDescription] = useState('');
  const [galleryAnnouncement, setGalleryAnnouncement] = useState('');
  const [galleryLoading, setGalleryLoading] = useState(false);
  
  // 图集创建成功弹框
  const [gallerySuccessModalOpen, setGallerySuccessModalOpen] = useState(false);
  const [createdGalleryUrl, setCreatedGalleryUrl] = useState('');

  // 批量复制链接加载状态
  const [batchCopyLoading, setBatchCopyLoading] = useState(false);
  const [batchDownloadLoading, setBatchDownloadLoading] = useState(false);
  const [batchDownloadProgressOpen, setBatchDownloadProgressOpen] = useState(false);
  const [batchDownloadProgress, setBatchDownloadProgress] = useState<BatchDownloadProgress>({
    current: 0,
    total: 0,
    success: 0,
    failed: 0,
  });
  const batchDownloadAbortRef = useRef<AbortController | null>(null);
  const batchDownloadCancelledRef = useRef(false);



  // 随机路径开关，持久化到本地存储
  const RANDOM_PATH_KEY = 'imgcloud_random_path_enabled';
  const [randomPathEnabled, setRandomPathEnabled] = useState(() => {
    const stored = localStorage.getItem(RANDOM_PATH_KEY);
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(RANDOM_PATH_KEY, String(randomPathEnabled));
  }, [randomPathEnabled]);

  const randomPathLabel = isPc
    ? (randomPathEnabled ? t('batchActions.randomPathEnabled') : t('batchActions.randomPathDisabled'))
    : t('batchActions.randomPath');

  const syncActiveDirectory = (dirs: string[], desiredDir: string) => {
    const nextDir = dirs.includes(desiredDir) ? desiredDir : (dirs[0] || '');
    if (nextDir !== internalDir) {
      setInternalDir(nextDir);
      saveActiveDirectory(nextDir);
      onDirChange(nextDir);
    }
  };

  // 拉取目录列表
  const fetchDirList = async (desiredDir = internalDir) => {
    setDirLoading(true);
    try {
      const res = await fetch('/img-dir/list');
      const json = await res.json();
      if (json.success) {
        const dirs = sortDirectories(Array.isArray(json.data) ? json.data : []);
        setDirList(dirs);
        setSharedDirectoryPaths(new Set(
          (Array.isArray(json.sharedDirectoryPaths) ? json.sharedDirectoryPaths : [])
            .filter((value: unknown): value is string => typeof value === 'string')
            .map(normalizeShareDirectory)
        ));
        syncActiveDirectory(dirs, desiredDir);
        return dirs;
      } else {
        message.error(json.message || t('batchActions.getDirectoryFailed'));
      }
    } catch (e) {
      message.error(t('batchActions.getDirectoryFailed'));
    } finally {
      setDirLoading(false);
    }

    return [];
  };

  useEffect(() => {
    fetchDirList();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const refresh = () => fetchDirList(internalDir);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('imgcloud:directory-share-changed', refresh);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('imgcloud:directory-share-changed', refresh);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalDir]);

  // 外部 currentDir 变化时同步 internalDir
  useEffect(() => {
    setInternalDir(currentDir);
  }, [currentDir]);

  const formatDirLabel = useCallback((value: string) => {
    if (value === '__all__' || value === '全部目录') return t('common.allDirectories');
    if (value === '__default__' || value === '默认目录') return t('common.defaultDirectory');
    return value;
  }, [t]);

  const dirTreeData = useMemo(() => {
    const tree = buildDirTree(dirList);
    const titledTree = mapDirTreeTitles(tree, formatDirLabel);
    const decorate = (nodes: typeof titledTree): any[] => nodes.map((node) => {
      const shared = !isAllDirectory(node.value)
        && sharedDirectoryPaths.has(normalizeShareDirectory(node.value));
      return {
        ...node,
        searchText: `${formatDirLabel(node.value)} ${node.value}`.toLowerCase(),
        title: (
          <span className="directory-share-tree-title">
            <span className="directory-share-tree-title__label">{node.title}</span>
            {shared && (
              <Tag color="success" className="directory-share-tree-title__tag">
                {t('directoryShare.sharedBadge')}
              </Tag>
            )}
          </span>
        ),
        children: node.children?.length ? decorate(node.children) : undefined,
      };
    });
    return decorate(titledTree);
  }, [dirList, formatDirLabel, sharedDirectoryPaths, t]);

  const moveTargetTreeData = useMemo(() => {
    const candidates = dirList.length > 0 ? dirList : ['默认目录'];
    const tree = buildDirTree(candidates.filter((dir) => !isAllDirectory(dir)));
    return mapDirTreeTitles(tree, formatDirLabel);
  }, [dirList, formatDirLabel]);

  const deleteDirectoryByName = async (dirName: string) => {
    if (!dirName) return false;

    if (isSystemDirectory(dirName)) {
      message.warning(t('batchActions.directoryCannotDelete', { dir: formatDirLabel(dirName) }));
      return false;
    }

    setDirLoading(true);
    try {
      const res = await fetch('/img-dir/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dirName }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || t('batchActions.directoryDeleteFailed'));
      message.success(t('batchActions.directoryDeleted'));
      await fetchDirList(internalDir === dirName ? '' : internalDir);
      return true;
    } catch (e: any) {
      message.error(e?.message || t('batchActions.directoryDeleteFailed'));
      return false;
    } finally {
      setDirLoading(false);
    }
  };

  // 删除目录操作
  const handleDeleteDir = async () => {
    if (!internalDir) return;

    if (isSystemDirectory(internalDir)) {
      message.warning(t('batchActions.directoryCannotDelete', { dir: formatDirLabel(internalDir) }));
      return;
    }

    Modal.confirm({
      title: t('batchActions.deleteDirectoryConfirm', { dir: internalDir }),
      onOk: () => deleteDirectoryByName(internalDir),
    });
  };

  // 并发控制函数
  const batchProcess = async (items: any[], processFn: (item: any) => Promise<any>, concurrency: number = 15) => {
    const results: any[] = [];
    const chunks = [];
    
    // 将数组分块，每块大小为并发数
    for (let i = 0; i < items.length; i += concurrency) {
      chunks.push(items.slice(i, i + concurrency));
    }
    
    // 逐块处理，每块内并发执行
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const chunkPromises = chunk.map(processFn);
      const chunkResults = await Promise.all(chunkPromises);
      results.push(...chunkResults);
      
      // 如果不是最后一块，等待一小段时间再处理下一块
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms 间隔
      }
    }
    
    return results;
  };

  const getSelectedFilesInSelectionOrder = useCallback(() => {
    return selectedIds
      .map(id => list.find(item => item.id === id))
      .filter(Boolean);
  }, [selectedIds, list]);

  const readBatchDownloadError = async (response: Response) => {
    try {
      const data = await response.json();
      return data?.message || t('batchActions.downloadAllFailed');
    } catch {
      return t('batchActions.downloadAllFailed');
    }
  };

  const waitForBatchDownloadRetry = async (milliseconds: number) => {
    const deadline = Date.now() + milliseconds;
    while (Date.now() < deadline) {
      if (batchDownloadCancelledRef.current) {
        throw new DOMException('Download cancelled', 'AbortError');
      }
      await new Promise(resolve => setTimeout(resolve, Math.min(250, deadline - Date.now())));
    }
  };

  const fetchPreparedDownload = async (item: PreparedDownloadItem, uid: string) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const controller = new AbortController();
      batchDownloadAbortRef.current = controller;
      const response = await fetch('/img/batch-download/file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, ticket: item.ticket }),
        signal: controller.signal,
      });

      if (response.status === 429 && attempt < 2) {
        const retryAfter = Number(response.headers.get('Retry-After'));
        const retryMilliseconds = Math.min(60, Math.max(1, Number.isFinite(retryAfter) ? retryAfter : 1)) * 1000;
        await waitForBatchDownloadRetry(retryMilliseconds);
        continue;
      }
      if (!response.ok) {
        throw new Error(await readBatchDownloadError(response));
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = item.fileName || `download-${item.id}`;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
      }
      return;
    }
  };

  const handleBatchDownload = async () => {
    const downloadIds = selectedIds
      .map(id => Number(id))
      .filter(id => Number.isInteger(id) && id > 0);
    if (downloadIds.length === 0 || downloadIds.length !== selectedIds.length) {
      message.warning(t('batchActions.noSelectedFiles'));
      return;
    }

    setBatchDownloadLoading(true);
    batchDownloadCancelledRef.current = false;
    let successCount = 0;
    let failedCount = 0;

    try {
      const uid = localStorage.getItem('fdd_uid') || '';
      const prepareResponse = await fetch('/img/batch-download/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, ids: downloadIds }),
      });
      if (!prepareResponse.ok) {
        throw new Error(await readBatchDownloadError(prepareResponse));
      }
      const preparePayload = await prepareResponse.json();
      const items = preparePayload?.data?.items as PreparedDownloadItem[];
      if (!Array.isArray(items) || items.length === 0) {
        throw new Error(t('batchActions.downloadAllFailed'));
      }

      setBatchDownloadProgress({ current: 0, total: items.length, success: 0, failed: 0 });
      setBatchDownloadProgressOpen(true);

      for (let index = 0; index < items.length; index += 1) {
        if (batchDownloadCancelledRef.current) break;
        setBatchDownloadProgress(prev => ({ ...prev, current: index + 1 }));
        try {
          await fetchPreparedDownload(items[index], uid);
          successCount += 1;
        } catch (error) {
          if (batchDownloadCancelledRef.current || (error instanceof DOMException && error.name === 'AbortError')) {
            break;
          }
          failedCount += 1;
          console.warn('批量下载文件失败:', items[index].id);
        }
        setBatchDownloadProgress({
          current: index + 1,
          total: items.length,
          success: successCount,
          failed: failedCount,
        });
      }

      if (batchDownloadCancelledRef.current) {
        message.info(t('batchActions.downloadAllCancelled', { count: successCount }));
      } else if (failedCount === 0) {
        message.success(t('batchActions.downloadAllSuccess', { count: successCount }));
      } else if (successCount > 0) {
        message.warning(t('batchActions.downloadAllPartial', {
          success: successCount,
          total: items.length,
          failed: failedCount,
        }));
      } else {
        message.error(t('batchActions.downloadAllFailed'));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('batchActions.downloadAllFailed');
      message.error(errorMessage);
    } finally {
      batchDownloadAbortRef.current = null;
      setBatchDownloadProgressOpen(false);
      setBatchDownloadLoading(false);
    }
  };

  const cancelBatchDownload = () => {
    batchDownloadCancelledRef.current = true;
    batchDownloadAbortRef.current?.abort();
  };

  const openMoveDirectoryModal = () => {
    const defaultTarget = internalDir && !isAllDirectory(internalDir) && internalDir !== '__default__' && internalDir !== 'default'
      ? internalDir
      : '默认目录';
    setMoveTargetDir(defaultTarget);
    setMoveModalOpen(true);
    if (dirList.length === 0) {
      fetchDirList(defaultTarget);
    }
  };

  const handleMoveDirectory = async () => {
    if (!moveTargetDir) {
      message.warning(t('batchActions.moveDirectoryTargetRequired'));
      return;
    }
    if (isAllDirectory(moveTargetDir)) {
      message.warning(t('batchActions.moveDirectoryAllNotAllowed'));
      return;
    }

    const movingIds = selectedIds.map(id => parseInt(id, 10)).filter((id) => Number.isFinite(id));
    if (movingIds.length === 0) {
      message.warning(t('batchActions.noSelectedFiles'));
      return;
    }

    setMoveLoading(true);
    try {
      const uid = localStorage.getItem('fdd_uid');
      const response = await fetch('/img/batchMoveDirectory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: movingIds,
          uid,
          targetDir: moveTargetDir,
        }),
      });
      const result = await response.json();

      if (result.success) {
        setMoveModalOpen(false);
        setSelectedIds([]);
        loadHistory();
        if (result.successCount === movingIds.length) {
          message.success(result.message || t('batchActions.moveDirectorySuccess'));
        } else {
          message.warning(result.message || t('batchActions.moveDirectoryPartialSuccess', {
            success: result.successCount,
            total: movingIds.length,
          }));
        }
      } else {
        message.error(result.message || t('batchActions.moveDirectoryFailedRetry'));
      }
    } catch (error) {
      console.error('批量迁移目录失败:', error);
      message.error(t('batchActions.moveDirectoryFailedRetry'));
    } finally {
      setMoveLoading(false);
    }
  };

  // 处理批量复制链接
  const handleBatchCopyLinks = async () => {
    setBatchCopyLoading(true);
    try {
      const selectedFiles = getSelectedFilesInSelectionOrder();
      const urls = selectedFiles.map(item => item.url).filter(Boolean);
      
      if (urls.length === 0) {
        message.warning(t('batchActions.noAvailableLinks'));
        return;
      }

      // 使用并发控制批量调用接口生成短链接
      const shortUrls = await batchProcess(urls, async (url) => {
        try {
          const res = await fetch(`/u/save?url=${encodeURIComponent(url)}`, {
            method: 'POST',
          });
          const json = await res.json();
          if (json.shortId) {
            return json.fullShortUrl || `https://z.wiki/u/${json.shortId}`;
          } else {
            throw new Error(t('batchActions.shortLinkGenerateFailed'));
          }
        } catch (error) {
          console.error('生成短链接失败:', url, error);
          return url; // 如果失败，返回原链接
        }
      }, 15); // 限制并发数为15

      const resultText = shortUrls.join('\n');
      
      await copyToClipboardAsync(resultText, t('batchActions.shortLinksCopied', { count: shortUrls.length }));
    } catch (error) {
      console.error('批量复制链接失败:', error);
      message.error(t('batchActions.batchCopyFailed'));
    } finally {
      setBatchCopyLoading(false);
    }
  };

  // 处理生成图集
  const handleGenerateGallery = async () => {
      if (!isAllImage) {
        message.warning(t('batchActions.onlyImagesCanGenerate'));
        return;
      }
      if (!galleryTitle.trim()) {
          message.warning(t('batchActions.galleryTitleRequired'));
          return;
      }
      
      // 校验Banner URL格式
      if (galleryBannerUrl.trim() && !isValidUrl(galleryBannerUrl.trim())) {
          message.warning(t('batchActions.galleryBannerUrlInvalid'));
          return;
      }

      setGalleryLoading(true);
      const names = selectedIds.map(id => {
        const file = list.find(item => item.id === id);
        return file ? file.url : null;
      }).filter(Boolean);

      // NOTE: The backend API now supports title, imgs, bannerUrl, description, and announcement.
      let res, json;
      try {
        res = await fetch('/gallery/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            title: galleryTitle.trim(), 
            imgs: names, 
            bannerUrl: galleryBannerUrl, 
            description: galleryDescription,
            announcement: galleryAnnouncement
          }),
        });
      } catch (e) {
        message.error(t('batchActions.galleryCreateFailedNetwork'));
        setGalleryLoading(false);
        return;
      }

      if (!res.ok) {
        // 尝试解析 message 字段
        try {
          const errJson = await res.json();
          if (errJson && errJson.message) {
            message.error(t('batchActions.galleryCreateFailed', { message: errJson.message }));
            setGalleryLoading(false);
            return;
          }
        } catch (e) {}
        message.error(t('batchActions.galleryCreateFailedStatus', { status: res.status }));
        setGalleryLoading(false);
        return;
      }

      try {
        json = await res.json();
      } catch (e) {
        message.error(t('batchActions.galleryCreateFailedFormat'));
        setGalleryLoading(false);
        return;
      }

      if (!json.success) {
        message.error(t('batchActions.galleryCreateFailed', { message: json.message || t('error.unknownError') }));
        setGalleryLoading(false);
        return;
      }

      const galleryUrl = json.data;

      setGenerateGalleryModalOpen(false);
      setGalleryTitle('');
      setGalleryBannerUrl('');
      setGalleryDescription('');
      setGalleryAnnouncement('');
      setGalleryLoading(false);

      // 显示成功弹框
      setCreatedGalleryUrl(galleryUrl);
      setGallerySuccessModalOpen(true);
      await copyToClipboardAsync(galleryUrl, t('batchActions.galleryLinkCopied'));
  };

  // 下拉菜单项
  const menuItems = [
    {
      key: 'manage',
      icon: <FolderOpenOutlined />,
      label: t('directoryManager.manage'),
      onClick: () => navigate(ROUTES.DIRECTORIES),
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('batchActions.deleteDirectory'),
      danger: true,
      disabled: !internalDir,
      onClick: handleDeleteDir,
    },
  ];

  // 目录选择和更多操作合并为 Dropdown.Button
  const dirSelect = (
      <Dropdown.Button
        type="default"
        size="small"
        style={{ width: 'auto' }}
        loading={dirLoading}
        menu={{ items: menuItems }}
        icon={<MoreOutlined />}
        placement="bottomRight"
        trigger={['click']}
        onClick={e => e.preventDefault()} // 防止主按钮点击
      >
        <TreeSelect
          style={{ minWidth: 120 }}
          value={internalDir || undefined}
          onChange={dir => {
            const nextDir = typeof dir === 'string' ? dir : '';
            (window as any)._dir = nextDir;
            setInternalDir(nextDir);
            saveActiveDirectory(nextDir);
            onDirChange(nextDir);
          }}
          placeholder={t('batchActions.selectDirectory')}
          size="small"
          suffixIcon={<FolderOpenOutlined />}
          loading={dirLoading}
          bordered={false}
          dropdownClassName="dir-tree-switcher-right"
          dropdownStyle={{ minWidth: 180 }}
          treeData={dirTreeData}
          treeDefaultExpandAll
          showSearch
          filterTreeNode={(input, node) => String(node.searchText || '').includes(input.toLowerCase())}
          onDropdownVisibleChange={(visible) => {
            if (visible) fetchDirList(internalDir);
          }}
          allowClear={false}
        />
      </Dropdown.Button>
  );

  // 判断选中项是否全部为图片
  const isAllImage = selectedIds.every(id => {
    const file = list.find(item => item.id === id);
    if (!file) return false;
    return isImg(file.url || '');
  });

  if (!selectedIds || selectedIds.length === 0) return (
    <>
    <div style={{margin: '16px 0', display: 'inline-flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'flex-end'}}>
        {dirSelect}
        <Tooltip title={t('batchActions.randomPathTooltip')}>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 12, marginLeft: 6 }}>
            <Switch
              checked={randomPathEnabled}
              onChange={setRandomPathEnabled}
              size="small"
              style={{ marginRight: 4 }}
            />
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {randomPathLabel}
            </span>
          </div>
        </Tooltip>
      <Input.Search
        placeholder={t('batchActions.searchPlaceholder')}
        allowClear
        enterButton={<SearchOutlined />}
        size="small"
        onChange={e => {
          onKeywordChange && onKeywordChange(e.target.value);
        }}
        onSearch={onSearch}
      />
    </div>
    </>
  );

  return (
    <>
      <div style={{margin: '16px 0', display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'flex-end'}}>
        <div style={{display: 'flex', width: '100%'}}>
        {dirSelect}
        <Tooltip title={t('batchActions.randomPathTooltip')}>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 12, marginLeft: 6 }}>
            <Switch
              checked={randomPathEnabled}
              onChange={setRandomPathEnabled}
              size="small"
              style={{ marginRight: 4 }}
            />
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {randomPathLabel}
            </span>
          </div>
        </Tooltip>
        </div>
        <Dropdown
          menu={{
            items: [
              // {
              //   key: 'soft-delete',
              //   label: '删除到回收站',
              //   onClick: async () => {
              //     const doDelete = window.confirm(`确定要删除所选的 ${selectedIds.length} 个文件吗？文件将移动到回收站，7 天后自动清理。`);
              //     if (!doDelete) return;
              //     try {
              //       await Promise.all(selectedIds.map(id =>
              //         fetch(`/img/delete?hard=false&id=${id}&uid=${localStorage.getItem('fdd_uid')}`, { method: 'DELETE' })
              //       ));
              //       setSelectedIds([]);
              //       loadHistory();
              //       message.success('所选文件已移动到回收站');
              //     } catch (error) {
              //       message.error('删除失败，请重试');
              //     }
              //   }
              // },
              {
                key: 'hard-delete',
                label: t('batchActions.deleteSelected'),
                danger: true,
                onClick: async () => {
                  const doDelete = window.confirm(t('batchActions.deleteSelectedConfirm', { count: selectedIds.length }));
                  if (!doDelete) return;
                  try {
                    const uid = localStorage.getItem('fdd_uid');
                    const ids = selectedIds.map(id => parseInt(id));
                    const response = await fetch(`/img/batchDelete`, { 
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ 
                        ids,
                        uid,
                        hard: true
                      })
                    });
                    const result = await response.json();
                    
                    if (result.success) {
                      setSelectedIds([]);
                      loadHistory();
                      if (result.successCount === selectedIds.length) {
                        message.success(result.message || t('batchActions.selectedFilesDeleted'));
                      } else {
                        message.warning(result.message || t('batchActions.deletePartialSuccess', { success: result.successCount, total: selectedIds.length }));
                      }
                    } else {
                      message.error(result.message || t('batchActions.deleteFailedRetry'));
                    }
                  } catch (error) {
                    console.error('批量删除失败:', error);
                    message.error(t('batchActions.deleteFailedRetry'));
                  }
                }
              },
            ]
          }}
        >
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={async () => {
              const doDelete = window.confirm(t('batchActions.deleteSelectedConfirm', { count: selectedIds.length }));
              if (!doDelete) return;
              try {
                const uid = localStorage.getItem('fdd_uid');
                const ids = selectedIds.map(id => parseInt(id));
                const response = await fetch(`/img/batchDelete`, { 
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ 
                    ids,
                    uid,
                    hard: true
                  })
                });
                const result = await response.json();
                
                if (result.success) {
                  setSelectedIds([]);
                  loadHistory();
                  if (result.successCount === selectedIds.length) {
                    message.success(result.message || t('batchActions.selectedFilesDeleted'));
                  } else {
                    message.warning(result.message || t('batchActions.deletePartialSuccess', { success: result.successCount, total: selectedIds.length }));
                  }
                } else {
                  message.error(result.message || t('batchActions.deleteFailedRetry'));
                }
              } catch (error) {
                console.error('批量删除失败:', error);
                message.error(t('batchActions.deleteFailedRetry'));
              }
            }}
            style={{ marginLeft: 8 }}
          >
            {t('batchActions.deleteSelected')}
          </Button>
        </Dropdown>
        <Button
          type="primary"
          size="small"
          icon={<FolderOpenOutlined />}
          style={{ marginLeft: 8 }}
          onClick={openMoveDirectoryModal}
        >
          {t('batchActions.moveDirectory')}
        </Button>
        <Button
          type="primary"
          size="small"
          style={{ marginLeft: 8 }}
          disabled={!isAllImage}
          title={!isAllImage ? t('batchActions.onlyImagesCanGenerate') : ''}
          onClick={() => setGenerateGalleryModalOpen(true)} // Open modal
        >
          {t('batchActions.generateGallery')}
        </Button>
        <Dropdown
          menu={{
            items: [
              {
                key: 'text',
                label: t('batchActions.textLinks'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const text = selectedFiles.map(item => item.url).join('\n');
                  await copyToClipboardAsync(text, t('batchActions.textLinksCopied'));
                }
              },
              {
                key: 'shortlinks',
                label: t('batchActions.shortLinks'),
                onClick: handleBatchCopyLinks,
                disabled: batchCopyLoading,
              },
              {
                key: 'webp',
                label: t('batchActions.losslessCompression'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const webpUrls = selectedFiles.map(item => {
                    const [pathname, search] = item.url.split('?');
                    return search ? `${pathname}/webp?${search}` : `${pathname}/webp`;
                  }).join('\n');
                  await copyToClipboardAsync(webpUrls, t('batchActions.losslessCompressionCopied'));
                }
              },
              {
                key: 'overseas',
                label: t('batchActions.overseasAcceleration'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const overseasUrls = selectedFiles.map(item => {
                    const urlObj = new URL(item.url);
                    return `https://xland.eu.org${urlObj.pathname}${urlObj.search}`;
                  }).join('\n');
                  await copyToClipboardAsync(overseasUrls, t('batchActions.overseasAccelerationCopied'));
                }
              },
              {
                key: 'thumbnail100',
                label: t('batchActions.thumbnail100px'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/100x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, t('batchActions.thumbnail100pxCopied'));
                }
              },
              {
                key: 'thumbnail200',
                label: t('batchActions.thumbnail200px'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/200x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, t('batchActions.thumbnail200pxCopied'));
                }
              },
              {
                key: 'thumbnail300',
                label: t('batchActions.thumbnail300px'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/300x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, t('batchActions.thumbnail300pxCopied'));
                }
              },
              {
                key: 'thumbnail600',
                label: t('batchActions.thumbnail600px'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/600x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, t('batchActions.thumbnail600pxCopied'));
                }
              },
              {
                key: 'watermark_default',
                label: t('batchActions.defaultWatermark'),
                onClick: async () => {
                  const storedText = getStoredWatermarkText();
                  const customText = window.prompt(t('batchActions.watermarkTextPrompt'), storedText);
                  if (customText) {
                    if (customText.length > 10) {
                      message.error(t('batchActions.watermarkTextTooLong'));
                      return;
                    }
                    saveWatermarkText(customText);
                    const selectedFiles = getSelectedFilesInSelectionOrder();
                    const watermarks = selectedFiles.map(item => {
                      const separator = item.url.includes('?') ? '&' : '?';
                      const text = urlSafeBase64Encode(customText);
                      return `${item.url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/t/50`;
                    }).join('\n');
                    await copyToClipboardAsync(watermarks, t('batchActions.defaultWatermarkCopied'));
                  }
                }
              },
              {
                key: 'watermark_tile',
                label: t('batchActions.tileWatermark'),
                onClick: async () => {
                  const storedText = getStoredWatermarkText();
                  const customText = window.prompt(t('batchActions.watermarkTextPrompt'), storedText);
                  if (customText) {
                    if (customText.length > 10) {
                      message.error(t('batchActions.watermarkTextTooLong'));
                      return;
                    }
                    saveWatermarkText(customText);
                    const selectedFiles = getSelectedFilesInSelectionOrder();
                    const watermarks = selectedFiles.map(item => {
                      const separator = item.url.includes('?') ? '&' : '?';
                      const text = urlSafeBase64Encode(customText);
                      return `${item.url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/dissolve/10/gravity/center/batch/1/degree/30`;
                    }).join('\n');
                    await copyToClipboardAsync(watermarks, t('batchActions.tileWatermarkCopied'));
                  }
                }
              },
              {
                key: 'image_watermark',
                label: t('batchActions.imageWatermark'),
                onClick: async () => {
                  const storedImageUrl = getStoredWatermarkImageUrl();
                  const watermarkImageUrl = window.prompt(t('batchActions.watermarkImagePrompt'), storedImageUrl || 'https://z.wiki/autoupload/20230831/l32P.%E5%9B%BE%E7%89%87%E5%8A%A0%E8%BD%BD%E5%A4%B1%E8%B4%A5_%282%29.svg');
                                      if (watermarkImageUrl) {
                      // 验证URL格式
                      if (!watermarkImageUrl.startsWith('http://') && !watermarkImageUrl.startsWith('https://')) {
                        message.error(t('batchActions.watermarkImageInvalid'));
                        return;
                      }

                    saveWatermarkImageUrl(watermarkImageUrl);
                    
                    // 从水印图片URL中截取路径
                    let imageKey = '';
                    try {
                      const urlObj = new URL(watermarkImageUrl);
                      // 提取路径部分，去掉域名
                      imageKey = urlObj.pathname;
                      // 如果路径以/开头，去掉开头的/
                      if (imageKey.startsWith('/')) {
                        imageKey = imageKey.substring(1);
                      }
                    } catch (e) {
                      message.error(t('batchActions.watermarkImageUrlError'));
                      return;
                    }
                    
                    const selectedFiles = getSelectedFilesInSelectionOrder();
                    const encodedImageKey = urlSafeBase64Encode(imageKey);
                    const watermarks = selectedFiles.map(item => {
                      const separator = item.url.includes('?') ? '&' : '?';
                      return `${item.url}${separator}watermark/1/image_key/${encodedImageKey}/gravity/southeast/ws/100/ht/50`;
                    }).join('\n');
                    await copyToClipboardAsync(watermarks, t('batchActions.imageWatermarkCopied'));
                  }
                }
              },
              {
                key: 'html',
                label: t('batchActions.html'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const html = selectedFiles.map(item => `<img src="${item.url}" />`).join('\n');
                  await copyToClipboardAsync(html, t('batchActions.htmlCopied'));
                }
              },
              {
                key: 'bbcode',
                label: t('batchActions.bbcode'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const bbcode = selectedFiles.map(item => `[img]${item.url}[/img]`).join('\n');
                  await copyToClipboardAsync(bbcode, t('batchActions.bbcodeCopied'));
                }
              },
              {
                key: 'markdown',
                label: t('batchActions.markdown'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const md = selectedFiles.map(item => `![](${item.url})`).join('\n');
                  await copyToClipboardAsync(md, t('batchActions.markdownCopied'));
                }
              },
            ]
          }}
        >
          <Button size="small" style={{ marginLeft: 8 }} loading={batchCopyLoading}>{t('batchActions.batchCopy')}</Button>
        </Dropdown>
        <Dropdown
          trigger={["hover"]}
          menu={{
            items: [
              {
                key: 'downloadAll',
                icon: <DownloadOutlined />,
                label: batchDownloadLoading
                  ? t('batchActions.downloadingAll')
                  : t('batchActions.downloadAll'),
                onClick: handleBatchDownload,
                disabled: batchDownloadLoading,
              },
              {
                type: 'divider',
              },
              {
                key: 'selectAll',
                label: t('batchActions.selectAll'),
                onClick: () => {
                  setSelectedIds(list.map(item => item.id));
                }
              },
              {
                key: 'invert',
                label: t('batchActions.invert'),
                onClick: () => {
                  setSelectedIds((prev: string[]) => list.map(item => item.id).filter((id: string) => !prev.includes(id)));
                }
              },
              {
                key: 'clear',
                label: t('batchActions.clearSelection'),
                onClick: () => {
                  setSelectedIds([]);
                }
              }
            ]
          }}
        >
          <Button size="small" style={{ marginLeft: 8 }}>{t('batchActions.moreActions')}</Button>
        </Dropdown>
      </div>

      <Modal
        title={t('batchActions.downloadProgressTitle')}
        open={batchDownloadProgressOpen}
        closable={false}
        maskClosable={false}
        footer={[
          <Button key="cancel" danger onClick={cancelBatchDownload}>
            {t('common.cancel')}
          </Button>
        ]}
      >
        <Progress
          percent={batchDownloadProgress.total > 0
            ? Math.round(((batchDownloadProgress.success + batchDownloadProgress.failed) / batchDownloadProgress.total) * 100)
            : 0}
          status="active"
        />
        <div style={{ marginTop: 12 }}>
          {t('batchActions.downloadProgress', batchDownloadProgress)}
        </div>
      </Modal>

      <Modal
        title={t('batchActions.moveDirectory')}
        open={moveModalOpen}
        onCancel={() => setMoveModalOpen(false)}
        onOk={handleMoveDirectory}
        confirmLoading={moveLoading}
        okText={t('batchActions.confirmMoveDirectory')}
        cancelText={t('common.cancel')}
      >
        <div style={{ marginBottom: 12 }}>
          {t('batchActions.moveDirectoryConfirmContent', { count: selectedIds.length })}
        </div>
        <TreeSelect
          style={{ width: '100%' }}
          value={moveTargetDir || undefined}
          onChange={(dir) => setMoveTargetDir(typeof dir === 'string' ? dir : '')}
          placeholder={t('batchActions.moveDirectoryTargetPlaceholder')}
          loading={dirLoading}
          dropdownClassName="dir-tree-switcher-right"
          treeData={moveTargetTreeData}
          treeDefaultExpandAll
          showSearch
          treeNodeFilterProp="title"
        />
      </Modal>

      {/* Modal for generating gallery */}
      <Modal
          title={t('batchActions.generateGallery')}
          open={generateGalleryModalOpen}
          onCancel={() => {
            setGenerateGalleryModalOpen(false);
            setGalleryTitle('');
            setGalleryBannerUrl('');
            setGalleryDescription('');
            setGalleryAnnouncement('');
            // 清空成功弹框状态
            setGallerySuccessModalOpen(false);
            setCreatedGalleryUrl('');
          }}
          onOk={handleGenerateGallery}
          confirmLoading={galleryLoading}
          okText={t('batchActions.generate')}
          cancelText={t('common.cancel')}
      >
          <div style={{ marginBottom: 16 }}>
              <label>{t('batchActions.galleryTitleLabel')}</label>
              <Input
                  value={galleryTitle}
                  onChange={e => setGalleryTitle(e.target.value)}
                  placeholder={t('batchActions.galleryTitlePlaceholder')}
              />
          </div>
          <div style={{ marginBottom: 16 }}>
              <label>{t('batchActions.galleryBannerLabel')}</label>
              <Input
                  value={galleryBannerUrl}
                  onChange={e => setGalleryBannerUrl(e.target.value)}
                  placeholder={t('batchActions.galleryBannerUrlPlaceholder')}
              />
          </div>
          <div style={{ marginBottom: 16 }}>
              <label>{t('batchActions.galleryDescriptionLabel')}</label>
              <Input.TextArea
                  value={galleryDescription}
                  onChange={e => setGalleryDescription(e.target.value)}
                  placeholder={t('batchActions.galleryDescriptionPlaceholder')}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  onPaste={e => e.stopPropagation()}
              />
          </div>
          <div style={{ marginBottom: 16 }}>
              <label>{t('batchActions.galleryAnnouncementLabel')}</label>
              <Input.TextArea
                  value={galleryAnnouncement}
                  onChange={e => setGalleryAnnouncement(e.target.value)}
                  placeholder={t('batchActions.galleryAnnouncementPlaceholder')}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  onPaste={e => e.stopPropagation()}
              />
          </div>
      </Modal>

      {/* 图集创建成功弹框 */}
      <Modal
        open={gallerySuccessModalOpen}
        title={t('batchActions.galleryCreateSuccess')}
        onCancel={() => {
          setGallerySuccessModalOpen(false);
          setCreatedGalleryUrl('');
        }}
        footer={[
          <Button 
            key="copy" 
            type="primary" 
            onClick={async () => {
              await copyToClipboardAsync(createdGalleryUrl, t('batchActions.galleryLinkCopied'));
            }}
          >
            {t('batchActions.copyLink')}
          </Button>,
                     <Button key="close" onClick={() => {
             setGallerySuccessModalOpen(false);
             setCreatedGalleryUrl('');
           }}>
             {t('common.close')}
           </Button>
        ]}
        width={500}
      >
        <div style={{ textAlign: 'left', padding: '20px 0' }}>
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#52c41a' }}>{t('batchActions.galleryLink')}</h4>
            <div style={{ 
              wordBreak: 'break-all', 
              userSelect: 'all', 
              fontSize: 14, 
              padding: 12, 
              background: '#f5f5f5', 
              borderRadius: 6,
              border: '1px solid #e0e0e0'
            }}>
              {createdGalleryUrl}
            </div>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px 0' }}>{t('batchActions.galleryQrCode')}</h4>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center',
              padding: 16,
              background: '#fafafa',
              borderRadius: 6,
              border: '1px solid #e0e0e0'
            }}>
              <img 
                src={`https://playground.z.wiki/qrcode/generateQRCode?data=${encodeURIComponent(createdGalleryUrl)}`}
                alt={t('batchActions.galleryQrCodeAlt')}
                style={{ 
                  width: 200, 
                  height: 200,
                  border: '1px solid #ddd',
                  borderRadius: 4
                }}
                onError={(e) => {
                  // 如果二维码加载失败，显示备用文本
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLImageElement).parentElement;
                  if (parent && !parent.querySelector('.qr-error')) {
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'qr-error';
                    errorDiv.style.cssText = 'color: #999; font-size: 14px; padding: 40px;';
                    errorDiv.textContent = t('batchActions.galleryQrCodeFailed');
                    parent.appendChild(errorDiv);
                  }
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              {t('batchActions.scanQrCodeHint')}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!manualCopyUrl}
        title={t('batchActions.manualCopyTitle')}
        onCancel={() => setManualCopyUrl(null)}
        footer={[
          <Button key="close" onClick={() => setManualCopyUrl(null)}>
            {t('common.close')}
          </Button>
        ]}
      >
        <div style={{ wordBreak: 'break-all', userSelect: 'all', fontSize: 16, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
          {manualCopyUrl}
        </div>
        <div style={{ marginTop: 12, color: '#888' }}>{t('batchActions.manualCopyHint')}</div>
      </Modal>
    </>
  );
};

export default BatchActions;
